import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { FullPageSpinner } from "@/lib/components/ui/spinner";
import { config } from "@/lib/config";
import {
  isTenantOnboardingDone,
  TENANT_MUTATION_FORBIDDEN_EVENT,
  type Tenant,
  type TenantWizardViewModel,
  useTenant,
  useTenantSetupWizardEligibility,
} from "@/modules/tenants/tenants.api";
import { syncTenantBrandCssVars } from "@/modules/tenants/tenants.colors";
import {
  TenantSetupDialog,
  TenantSetupDialogContent,
} from "@/modules/tenants/components/tenant-setup-dialog";
import { noop } from "@tanstack/react-query";

export type TenantsContextValue = {
  tenant?: Tenant;
  openTenantSetup: () => void;
  wizardViewModel?: TenantWizardViewModel;
  canUseTenantSetup: boolean;
  /**
   * True when company setup is done, dismissed this session, or unavailable —
   * the module tour may auto-offer once this is settled.
   */
  isCompanySetupSettled: boolean;
  /** True while TenantSetupDialog is open (auto or manual). */
  isTenantSetupDialogOpen: boolean;
};

const TenantsContext = createContext({} as TenantsContextValue);

export function useTenantsContext() {
  return useContext(TenantsContext);
}

export function TenantsProvider({ children }: { children: ReactNode }) {
  const tenantQuery = useTenant();
  const needsTenantFetch = tenantQuery.isEntitled;

  if (needsTenantFetch && tenantQuery.isPending) {
    return <FullPageSpinner className="min-h-svh" />;
  }

  if (needsTenantFetch && tenantQuery.isError) {
    throw tenantQuery.error instanceof Error
      ? tenantQuery.error
      : new Error("Failed to load tenant", { cause: tenantQuery.error });
  }

  return (
    <TenantsProviderContent
      tenant={tenantQuery.tenant}
      isPending={tenantQuery.isPending}
      wizardViewModel={tenantQuery.wizardViewModel}
    >
      {children}
    </TenantsProviderContent>
  );
}

type TenantsProviderContentProps = {
  tenant?: Tenant;
  children: ReactNode;
  isPending: boolean;
  wizardViewModel?: TenantWizardViewModel;
};

function TenantsProviderContent({
  tenant,
  children,
  isPending,
  wizardViewModel,
}: TenantsProviderContentProps) {
  const { canOpenWizard } = useTenantSetupWizardEligibility();

  const [manualSessionOpen, setManualSessionOpen] = useState(false);
  // closing the setup sticks across reloads, so it doesn't pop up on every visit
  // without a company there is nothing to remember it for
  const dismissedKey = tenant
    ? `${config.key}.companySetup.dismissed.${tenant.id}`
    : undefined;
  const [userDismissedAuto, setUserDismissedAuto] = useState(() =>
    readFlag(dismissedKey),
  );

  const onboardingDone = isTenantOnboardingDone(
    wizardViewModel?.company_onboarding,
  );

  const shouldOfferAutoOpen =
    canOpenWizard && !isPending && tenant !== undefined && !onboardingDone;

  const autoPanelOpen = shouldOfferAutoOpen && !userDismissedAuto;
  const dialogOpen = autoPanelOpen || manualSessionOpen;

  useEffect(function subscribeTenantMutationForbidden() {
    function handleTenantForbidden() {
      setManualSessionOpen(false);
      setUserDismissedAuto(true);
    }
    window.addEventListener(
      TENANT_MUTATION_FORBIDDEN_EVENT,
      handleTenantForbidden,
    );
    return function unsubscribeTenantMutationForbidden() {
      window.removeEventListener(
        TENANT_MUTATION_FORBIDDEN_EVENT,
        handleTenantForbidden,
      );
    };
  }, []);

  useEffect(
    function syncTenantBrandColorsToDocument() {
      syncTenantBrandCssVars(tenant);
      return function clearTenantBrandColorsFromDocument() {
        syncTenantBrandCssVars(undefined);
      };
    },
    [tenant],
  );

  const handleOpenChange = useCallback(
    function handleTenantSetupOpenChange(next: boolean) {
      if (!next) {
        setManualSessionOpen(false);
        if (!onboardingDone) {
          setUserDismissedAuto(true);
          writeFlag(dismissedKey);
        }
      }
    },
    [dismissedKey, onboardingDone],
  );

  const requestOpenTenantSetup = useCallback(
    function handleRequestOpenTenantSetup() {
      setManualSessionOpen(true);
    },
    [],
  );

  const isCompanySetupSettled =
    !canOpenWizard || onboardingDone || userDismissedAuto;

  const contextValue = useMemo(
    function computeTenantsContext() {
      return {
        tenant,
        openTenantSetup: canOpenWizard ? requestOpenTenantSetup : noop,
        wizardViewModel,
        canUseTenantSetup: canOpenWizard,
        isCompanySetupSettled,
        isTenantSetupDialogOpen: dialogOpen,
      } satisfies TenantsContextValue;
    },
    [
      canOpenWizard,
      dialogOpen,
      isCompanySetupSettled,
      requestOpenTenantSetup,
      tenant,
      wizardViewModel,
    ],
  );

  return (
    <TenantsContext.Provider value={contextValue}>
      {children}
      {canOpenWizard ? (
        <TenantSetupDialog
          open={dialogOpen}
          onOpenChange={handleOpenChange}
          wizardEnabled={canOpenWizard}
        >
          <TenantSetupDialogContent />
        </TenantSetupDialog>
      ) : null}
    </TenantsContext.Provider>
  );
}

function readFlag(key: string | undefined) {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    // no storage (private mode), so it just asks again next visit
    return false;
  }
}

function writeFlag(key: string | undefined) {
  if (!key) return;
  try {
    localStorage.setItem(key, "true");
  } catch {
    // no storage, nothing to remember
  }
}
